import React, { ReactElement } from "react";
import { useGetSeverityLabelsQuery } from "../../../__generated__/api";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

export interface SeveritiesPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    isClearable?: TIsClearable;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        texts: (TIsMulti extends true ? string[] : string)
    ) => void;
}

const Severities = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, isClearable,
}: SeveritiesPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetSeverityLabelsQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<string>[] : Option<string>;
    type ValueType = TIsMulti extends true ? Option<string>[] : Option<string>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<string>).value) as Parameters<typeof onSelected>[0];

        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label)
            : (selected as Option<string>).label) as Parameters<typeof onSelected>[1];
        onSelected(values, texts);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.settings?.sla?.severityLabels?.map((label, index) => ({
        label,
        value: index.toString(),
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    const options: (Option<string> | OptionWithChildren<string>)[] = values;

    return (

        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={(options ?? []) as Option<string>[]}
            value={selected as ValueType}
            isClearable={isClearable}

        />

    );
});

Severities.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
};

export default Severities as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: SeveritiesPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Severities>;
